import { ALLOWED_TIMEZONES, TIMEZONES } from '../constants/timezone.constant';
/**
 * Converts a 12-hour formatted time string to a 24-hour format.
 *
 * @param {string} dateString - The date string to be converted, in any format that can be parsed by the Date constructor.
 * @returns {string} - The time in 24-hour format (HH:MM).
 */
export function convertTo24HourFormat(dateString: string): string {
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0'); // Ensure two digits
  const minutes = date.getMinutes().toString().padStart(2, '0'); // Ensure two digits
  return `${hours}:${minutes}`;
}

/**
 * Formats a Date object to a string in ISO format with a fixed time of midnight in IST.
 *
 * @param {Date} date - The Date object to be formatted.
 * @returns {string} - The formatted date string in the format YYYY-MM-DD 00:00:00 +0530.
 */
export function formatDateIST(date: Date | string): string {
  // Check if the date is already a Date object; if not, convert it from string
  const dateObj =
    typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;

  // Convert the date to IST (+0530)
  const offset = 5 * 60 + 30; // IST offset in minutes (+05:30)
  const localDate = new Date(dateObj.getTime() + offset * 60 * 1000); // Add the offset to the date

  // Format the date as 'YYYY-MM-DD 00:00:00 +0530'
  return `${localDate.toISOString().split('T')[0]} 00:00:00 +0530`;
}

/**
 * Converts a 12-hour time string to a Date object in the local timezone.
 *
 * @param {string} datetime12h - The time string in 12-hour format (e.g., "02:30 PM").
 * @returns {Date} - The corresponding Date object representing the combined date and time.
 */
export function convertTimeToDateInLocalTimeZone(datetime12h: string): Date {
  // Get the current date
  const currentDate = new Date();
  const currentDateString = currentDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD

  // Combine the current date with the time from the API
  const datetimeString = `${currentDateString} ${datetime12h}`;

  // Convert the combined string into a Date object
  const dateObject = new Date(datetimeString);

  return dateObject;
}

export function convertTo12HourFormat(isoDate: string): string {
  const date = new Date(isoDate); // Parse the ISO string into a Date object

  // Get the UTC hours and minutes
  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0'); // Get minutes in UTC

  // Determine AM/PM
  const amPm = hours >= 12 ? 'PM' : 'AM';

  // Convert to 12-hour format
  hours = hours % 12;
  hours = hours || 12; // Handle 12:00 PM case (0 hour in 12-hour format becomes 12)

  // Return the formatted string in 12-hour format
  return `${hours}:${minutes} ${amPm}`;
}

export function convertDateTimeToIST(isoDate: string): string {
  const date = new Date(isoDate); // Parse the ISO string into a Date object

  // Extract year, month, day, hours, minutes, seconds in local time (which is already IST)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  // Return the formatted IST date in "YYYY-MM-DD HH:mm:ss +0530" format
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} +0530`;
}

/**
 * Extracts date or date+time from an ISO string, formatted in IST timezone.
 *
 * @param {string} isoDate - The ISO date string.
 * @param {boolean} [includeTime=true] - Whether to include time in the result.
 * @returns {string} - The formatted date string.
 */
export function getOnlyDateFromIso(
  isoDate: string,
  options: {
    includeTime?: boolean;
    format?: 'YYYY-MM-DD' | 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'custom';
    customFormatFn?: (date: Date) => string;
  } = {}
): string {
  const { includeTime = true, format = 'YYYY-MM-DD', customFormatFn } = options;
  const date = new Date(isoDate);

  // Extract components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  let formattedDate = '';

  switch (format) {
    case 'DD-MM-YYYY':
      formattedDate = `${day}-${month}-${year}`;
      break;
    case 'MM-DD-YYYY':
      formattedDate = `${month}/${day}/${year}`;
      break;
    case 'YYYY-MM-DD':
    default:
      formattedDate = `${year}-${month}-${day}`;
      break;
  }

  if (!includeTime) {
    return formattedDate;
  }

  // Support full datetime format
  const formattedTime = `${hours}:${minutes}:00 +0530`;

  if (format === 'custom' && typeof customFormatFn === 'function') {
    return customFormatFn(date);
  }

  return `${formattedDate} ${formattedTime}`;
}

/**
 * Utility function to format a given Date or date string
 * into the desired "YYYY-MM-DD HH:mm" format.
 *
 * @purpose
 * This function ensures that all date objects (or date strings)
 * are consistently formatted in the application, regardless of
 * the timezone or input type. Useful when working with APIs or
 * databases that require uniform date formats.
 *
 * @param dateInput - Can be a JavaScript Date object,
 *                    or a date string (e.g., "Thu Sep 18 2025 00:00:00 GMT+0530 (India Standard Time)")
 *
 * @returns A formatted date string in "YYYY-MM-DD HH:mm" format
 *
 * @example
 * const rawDate = "Thu Sep 18 2025 00:00:00 GMT+0530 (India Standard Time)";
 * const formattedDate = formatDateToStandard(rawDate);
 * console.log(formattedDate);
 * // Output: "2025-09-18 00:00"
 *
 * @example
 * const jsDate = new Date(2025, 8, 18, 0, 0); // Month is 0-indexed → 8 means September
 * const formattedDate = formatDateToStandard(jsDate);
 * console.log(formattedDate);
 * // Output: "2025-09-18 00:00"
 */
export function formatDateToSimple(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

/**
 * Extracts the `YYYY-MM-DD` portion from a date string.
 *
 * - Accepts pure date-only strings ("YYYY-MM-DD")
 * - Also accepts ISO-like strings that start with "YYYY-MM-DD" (e.g. "YYYY-MM-DDTHH:mm:ssZ")
 *
 * Returns `null` if the pattern doesn't match.
 */
export function extractDateOnly(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/**
 * Converts a local `Date` object to a stable "date-only" string `YYYY-MM-DD`.
 *
 * This is the recommended representation for DOB-like fields to avoid timezone
 * shifts when serializing to JSON / parsing on the server.
 */
export function serializeDateOnlyLocal(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Parses a date-only value into a local `Date` at local midnight.
 *
 * - If `value` is `YYYY-MM-DD` (or starts with it), it is parsed safely without UTC shifting.
 * - Otherwise, falls back to `new Date(value)` and then normalizes to local Y/M/D.
 *
 * Useful when hydrating date-pickers from API values that may be either
 * date-only strings or ISO timestamps.
 */
export function parseDateLikeToLocalDate(value: string): Date | null {
  const dateOnly = extractDateOnly(value);
  if (dateOnly) {
    const [y, m, d] = dateOnly.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) {
    return null;
  }
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

/**
 * Determines whether two timestamp strings represent the same calendar date.
 *
 * This method compares only the date portion (year, month, day) of the timestamps,
 * ignoring the time components. It's useful for checking if two events occurred
 * on the same day regardless of their specific times.
 *
 * @param previousTimestamp - The previous timestamp string to compare (ISO format or any valid date string)
 * @param currentTimestamp - The current timestamp string to compare (ISO format or any valid date string)
 * @returns True if both timestamps represent the same calendar date, false otherwise
 *
 * @example
 * ```typescript
 * // Returns true - same date, different times
 * isSameDate('2024-01-15T09:30:00Z', '2024-01-15T18:45:00Z');
 *
 * // Returns false - different dates
 * isSameDate('2024-01-15T23:59:59Z', '2024-01-16T00:00:01Z');
 * ```
 *
 * @throws Will throw an error if either timestamp string is invalid and cannot be parsed by Date constructor
 */
export const isSameDate = (
  previousTimestamp: string,
  currentTimestamp: string
): boolean => {
  const date1 = new Date(previousTimestamp);
  const date2 = new Date(currentTimestamp);
  return date1.toDateString() === date2.toDateString();
};

/**
 * Converts a UTC datetime string into a local `Date` object where only the time part
 * (hours and minutes) from the UTC value is preserved, and the date part is irrelevant.
 *
 * 📌 Usage Context:
 * This is primarily used with time-only pickers like PrimeNG's <p-calendar [timeOnly]="true" />,
 * where only the time portion of the Date object is rendered.
 *
 * 📌 Example:
 * If the server sends "2025-07-01T03:30:00Z", this function extracts the `03:30` UTC clock time,
 * and returns a local Date object like `2025-07-01T03:30:00.000+05:30` (IST),
 * which renders **03:30 AM** correctly in a time-only input — without timezone shift.
 *
 * ⚠️ Note:
 * The returned `Date` is a full Date object (with date and time), but only the time portion is used
 * for display when bound to a time-only UI component.
 *
 * @param utcString - The UTC datetime string (e.g., "2025-07-01T03:30:00Z")
 * @returns A Date object with the time set to the UTC clock time, interpreted in the local timezone
 */
export function createLocalTimeFromUTCClock(utcString: string): Date {
  const utcDate = new Date(utcString);

  const utcHours = utcDate.getUTCHours();
  const utcMinutes = utcDate.getUTCMinutes();

  const localDate = new Date();
  localDate.setHours(utcHours, utcMinutes, 0, 0);

  return localDate;
}

/**
 * Converts a local time string (e.g., from a time-only UI input) to a UTC ISO string,
 * while preserving the local clock time (hours, minutes, seconds).
 *
 * This is useful when the backend expects UTC-formatted timestamps,
 * but you want to preserve what the user selected visually (e.g., "03:30").
 *
 * 🧠 Example:
 * If local time string is "2025-07-01T03:30:00", and your timezone is IST (UTC+5:30),
 * normally converting to UTC would result in "2025-06-30T22:00:00.000Z".
 * But this function will return: "2025-07-01T03:30:00.000Z"
 *
 * That way, the **clock time remains 03:30**, even in UTC.
 *
 * 📌 Intended for time-only inputs (like PrimeNG <p-calendar [timeOnly]="true" />)
 *
 * @param localTimeString - A local datetime string (e.g., "2025-07-01T03:30:00")
 * @returns A UTC-formatted ISO string with the same local clock time (e.g., "2025-07-01T03:30:00.000Z")
 */

export function convertLocalTimeToUTCPreservingTime(
  localTimeString: string
): string {
  if (!localTimeString) {
    return '';
  }
  const date = new Date(localTimeString);

  // Get the local time values
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  // Create UTC string with the same time values
  // Using toISOString format but with local time values
  const utcString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.000Z`;
  return utcString;
}

/**
 * Formats a date string or Date object into a human-readable string
 * using a specific timezone and 12-hour format.
 *
 * @param dateInput - A Date object or string (ISO or parseable).
 * @param timeZone - IANA time zone string like 'US/Central', 'Asia/Kolkata', etc.
 * @returns {string} - Formatted string like '03 Jul 2025 05:45 PM'
 */
export function formatDateInTimezone(
  dateInput: Date | string,
  timeZone: string
): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) {
    return '';
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const parts = formatter.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';

  const day = get('day');
  const month = parseInt(get('month'), 10);
  const year = get('year');
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = get('dayPeriod');

  const monthName = new Date(0, month - 1).toLocaleString('default', {
    month: 'short',
  });

  return `${day} ${monthName} ${year} ${hour}:${minute} ${dayPeriod.toUpperCase()}`;
}

/**
 * Formats an ISO datetime string into a user-facing, localized string
 * with a short month, numeric day, and 12-hour time (hh:mm AM/PM).
 *
 * 📌 Usage Context:
 * Use this for displaying datetimes in the **UI layer** — such as call logs,
 * tables, or activity feeds — where a concise, human-readable format is required.
 *
 * 📌 Example:
 * Input:  "2025-09-24T14:35:00Z"
 * Output: "Sep 24, 2:35 PM"  (localized to user's timezone)
 *
 * ⚠️ Note:
 * - Uses the browser's local timezone by default.
 * - If strict UTC is required, use a dedicated UTC formatter instead.
 *
 * @param dateTime - An ISO datetime string (e.g., `"2025-09-24T14:35:00Z"`)
 * @returns A localized short date-time string (e.g., "Sep 24, 2:35 PM")
 */
export function formatDateTimeToLocalizedForUI(dateTime: string): string {
  return new Date(dateTime).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
/**
 * ⏱️ Converts a duration (in seconds) into a human-readable string.
 *
 * - If duration < 60 seconds → returns "X Seconds"
 * - If duration < 3600 seconds → returns "X Minutes Y Seconds"
 * - Otherwise → returns "X Hours Y Minutes Z Seconds"
 *
 * This is useful for displaying call durations, video playback lengths, timers, etc.
 *
 * 🧠 Example:
 * - formatDuration(45) → "45 Seconds"
 * - formatDuration(1920) → "32 Minutes"
 * - formatDuration(3675) → "1 Hour 1 Minute 15 Seconds"
 *
 * @param durationInSeconds - Duration in seconds (integer)
 * @returns Human-readable duration string
 */
export function formatDuration(durationInSeconds: number): string {
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = durationInSeconds % 60;

  if (hours > 0) {
    return `${hours} Hour${hours > 1 ? 's' : ''} ${minutes} Minute${
      minutes > 1 ? 's' : ''
    } ${seconds} Second${seconds > 1 ? 's' : ''}`;
  } else if (minutes > 0) {
    return `${minutes} Minute${minutes > 1 ? 's' : ''}${
      seconds > 0 ? ` ${seconds} Second${seconds > 1 ? 's' : ''}` : ''
    }`;
  } else {
    return `${seconds} Second${seconds > 1 ? 's' : ''}`;
  }
}

/**
 * 🕒 Calculates a start–end time range string from a given timestamp and duration.
 *
 * - Parses the given ISO timestamp as the start time.
 * - Adds `durationInSeconds` to compute the end time.
 * - Formats both start and end times in **12-hour clock format with AM/PM**.
 *
 * This is useful for showing call logs, meeting times, and session ranges.
 *
 * 🧠 Example:
 * - timestamp = "2025-08-28T20:29:50.039+00:00"
 * - durationInSeconds = 1920 (32 minutes)
 *
 * calculateTimeRange(timestamp, durationInSeconds)
 * 👉 "8:29 PM - 9:01 PM"  (in local timezone)
 *
 * @param timestamp - ISO datetime string (e.g., "2025-08-28T20:29:50.039+00:00")
 * @param durationInSeconds - Duration in seconds
 * @returns A string in the format "hh:mm AM/PM - hh:mm AM/PM"
 */
export function calculateTimeRange(
  timestamp: string,
  durationInSeconds: number
): string {
  const startDate = new Date(timestamp);
  const endDate = new Date(startDate.getTime() + durationInSeconds * 1000);

  const formatTime = (date: Date) => {
    const hours = date.getHours() % 12 || 12;
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
    return `${hours}:${minutes} ${ampm}`;
  };

  return `${formatTime(startDate)} - ${formatTime(endDate)}`;
}

/**
 * Returns the application-specific display timezone based on the user's system settings.
 *
 * This function reads the system (browser/OS) timezone using the Intl API and
 * normalizes it to one of the **allowed timezone values** used by the application
 * (e.g., "US/Mountain", "Asia/Kolkata").
 *
 * @returns {string} The normalized timezone identifier for display,
 *                   or an empty string if unsupported.
 */
export function getSystemDisplayTimeZone(): string {
  const systemTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const mapped = ALLOWED_TIMEZONES[systemTZ];
  if (mapped && TIMEZONES.has(mapped)) {
    return mapped;
  }

  return '';
}

/**
 * **Requirement:** Show the user’s operating-system / browser timezone next to
 * date-time fields (e.g. social “Create Post” schedule) so the label matches
 * how the calendar interprets values (GST, EST, IANA id, etc.).
 *
 * **Behavior:** Reads `Intl.DateTimeFormat().resolvedOptions().timeZone` and
 * resolves a human-readable name using `timeZoneName: 'long'` then `'short'`,
 * falling back to the IANA id, then `'Local time'`.
 *
 * **Examples (illustrative; actual strings depend on OS/locale):**
 * - Current value (internal): `America/New_York`
 * - After formatting: `"Eastern Standard Time"` or `"EST"`
 *
 * @param referenceDate - Optional instant used when resolving the zone name
 *   (DST-aware). Defaults to `new Date()`.
 * @returns A string suitable for UI copy in parentheses, e.g. schedule hint.
 */
export function getSystemTimeZoneDisplayLabel(
  referenceDate: Date = new Date()
): string {
  const iana = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const longName =
    new Intl.DateTimeFormat(undefined, { timeZoneName: 'long' })
      .formatToParts(referenceDate)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';

  const shortName =
    new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(referenceDate)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';

  return longName || shortName || iana || 'Local time';
}

/**
 * **Requirement:** APIs that expect a wall-clock datetime with an explicit
 * offset must receive the user’s **local** date/time (not a fixed IST string).
 * Used when building `FormData` for scheduled posts from a `Date` from the UI.
 *
 * **Behavior:** Formats the given `Date` using local getters (`getFullYear`,
 * `getHours`, …) and appends the offset from `getTimezoneOffset()` as `±HHMM`
 * (no colon), matching the previous `+0530`-style contract but for any zone.
 *
 * **Examples:**
 * - Current value: `Date` representing Apr 21, 2026 14:30:00 in Dubai (UTC+4)
 * - After formatting: `"2026-04-21 14:30:00 +0400"`
 * - Current value: `Date` in New York during EST (UTC−5)
 * - After formatting: `"2026-04-21 09:15:00 -0500"`
 *
 * @param d - A `Date` from a date-time picker (local interpretation).
 * @returns String `YYYY-MM-DD HH:mm:ss ±HHMM`
 */
export function formatDateTimeWithLocalOffset(d: Date): string {
  const pad2 = (n: number) => String(n).padStart(2, '0');

  const year = d.getFullYear();
  const month = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  const hours = pad2(d.getHours());
  const minutes = pad2(d.getMinutes());
  const seconds = pad2(d.getSeconds());

  // getTimezoneOffset(): minutes behind UTC (e.g. IST => -330)
  const offsetMinutes = -d.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const offH = pad2(Math.floor(abs / 60));
  const offM = pad2(abs % 60);

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${sign}${offH}${offM}`;
}

/**
 * Formats a Date object into a 24-hour time string.
 *
 * 📌 Purpose:
 * Converts a JavaScript Date (typically coming from a time-only UI picker)
 * into a plain "HH:mm" string expected by the backend.
 *
 * @param date - JavaScript Date object
 * @returns Time string in "HH:mm" format (e.g., "09:30", "18:45")
 */
export function formatTimeToHHmm(date: Date): string {
  return `${date.getHours().toString().padStart(2, '0')}:${date
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
}

/**
 * Formats a Date object or ISO date string into "dd MMM yyyy" format in IST timezone.
 *
 * 📌 Purpose:
 * Converts date picker values or backend UTC timestamps into the consistent
 * "dd MMM yyyy" format required by the API (e.g., "21 Feb 2025").
 *
 * 📌 Example:
 * Input:  "2025-02-20T18:30:00.000Z"
 * Output: "21 Feb 2025"  (converted to IST)
 *
 * ⚠️ Note:
 * - Accepts both Date objects and ISO/parseable date strings
 * - Automatically applies IST timezone (+05:30)
 * - Day is always zero-padded (dd)
 *
 * @param isoDate - ISO datetime string (UTC format) or a Date object
 * @returns Formatted date string in "dd MMM yyyy"
 */
export function formatIsoToReadableIST(isoDate: string | Date): string {
  const date = isoDate instanceof Date ? isoDate : new Date(isoDate);

  if (isNaN(date.getTime())) {
    return '';
  }

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Apply IST offset (+05:30) to get the correct calendar date
  const istOffsetMs = (5 * 60 + 30) * 60 * 1000;
  const istDate = new Date(date.getTime() + istOffsetMs);

  const day = String(istDate.getUTCDate()).padStart(2, '0');
  const month = months[istDate.getUTCMonth()];
  const year = istDate.getUTCFullYear();

  return `${day} ${month} ${year}`;
}
